import styled from 'styled-components';

export const Page = styled.div`
  background: ${({ theme }) => theme.colors.background};
`;

export const Hero = styled.section`
  position: relative;
  min-height: 420px;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 24px;
  align-items: flex-start;
  padding: 48px 16px 40px;
  max-width: ${({ theme }) => theme.container};
  margin: 0 auto;
  ${({ theme, $bgImage }) =>
    $bgImage
      ? `
    background-image: url(${$bgImage});
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;
  `
      : `
    background-image: linear-gradient(
      135deg,
      ${theme.colors.green[800]} 0%,
      ${theme.colors.green[700]} 40%,
      ${theme.colors.green[500]} 100%
    );
  `};
  border-radius: 0 0 24px 24px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  overflow: hidden;
  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    min-height: 360px;
  }
`;

export const HeroText = styled.div`
  padding: 24px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  h1 { margin: 0 0 10px 0; font-size: 2.4rem; color: #ffffff; }
  p { margin: 0; font-size: 1.1rem; color: #f9fafb; }
`;

export const HeroButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0;
  padding: 12px 24px;
  background-color: ${({ theme }) => theme.colors.orange[600]};
  color: #ffffff;
  text-decoration: none;
  font-weight: 600;
  border-radius: 8px;
  transition: background 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);

  &:hover {
    background-color: ${({ theme }) => theme.colors.orange[700]};
  }
`;

export const HeroButtonSecondary = styled(HeroButton)`
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.7);
  color: #ffffff;
  box-shadow: none;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

export const HeroActions = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const HeroHighlights = styled.ul`
  margin: 16px 0 0;
  padding-left: 18px;
  color: #f9fafb;
  line-height: 1.6;
  li {
    margin-bottom: 6px;
  }
`;

export const HeroImage = styled.div`
  width: 100%;
  height: 360px;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.green[50]} 0%, ${({ theme }) => theme.colors.orange[50]} 100%);
  position: relative;

  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

export const Dots = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 2;
`;

export const Dot = styled.button`
  width: 10px; height: 10px; border-radius: 50%; border: none; cursor: pointer;
  background: ${({ active, theme }) => active ? theme.colors.green[600] : theme.colors.gray[300]};
`;

export const SlideArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(0, 0, 0, 0.35);
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  z-index: 2;
  transition: background 0.15s ease-in-out, transform 0.15s ease-in-out;

  &:hover {
    background: rgba(0, 0, 0, 0.55);
    transform: translateY(-50%) scale(1.04);
  }

  ${({ $direction }) => $direction === 'prev' ? 'left: 16px;' : 'right: 16px;'}

  @media (max-width: 640px) {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }
`;

export const Section = styled.section`
  max-width: ${({ theme }) => theme.container};
  margin: 32px auto 0;
  padding: 24px 16px 32px;
  ${({ $variant, theme }) => $variant === 'muted' && `
    background: ${theme.colors.gray[100]};
    border-radius: 16px;
  `}
  ${({ $variant, theme }) => $variant === 'cta' && `
    background: linear-gradient(135deg, ${theme.colors.orange[500]}, ${theme.colors.orange[300]});
    border-radius: 16px;
    color: #ffffff;
  `}
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray[200]};
  border-radius: 12px;
  padding: 18px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  h3 { margin: 0 0 8px 0; color: ${({ theme }) => theme.colors.green[700]}; }
  p { margin: 0; color: ${({ theme }) => theme.colors.text}; }
`;

export const Info = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

export const ContactLayout = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 24px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const ContactCard = styled.div`
  background: ${({ theme, $tone }) =>
    $tone === 'details'
      ? `linear-gradient(135deg, #1a1f2e, #0f1419)`
      : 'rgba(255, 255, 255, 0.98)'};
  border-radius: 16px;
  padding: 20px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  border: ${({ $tone, theme }) =>
    $tone === 'details' ? `1px solid rgba(255, 255, 255, 0.1)` : 'none'};
  color: ${({ $tone }) => ($tone === 'details' ? '#f9fafb' : 'inherit')};
`;

export const ContactDetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

export const ContactDetailIcon = styled.div`
  font-size: 22px;
  color: ${({ theme }) => theme.colors.orange[400]};
  margin-top: 2px;
`;

export const ContactDetailText = styled.div`
  h3 {
    margin: 0 0 4px 0;
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #ffffff;
  }
  p {
    margin: 0;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.95rem;
    color: #e5e7eb;
  }
`;

export const ContactField = styled.div`
  margin-bottom: 12px;
  label { display: block; margin-bottom: 6px; color: ${({ theme }) => theme.colors.text}; }
  input, textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid ${({ theme }) => theme.colors.gray[300]};
    border-radius: 10px;
    background: #ffffff;
  }
  textarea {
    min-height: 120px;
    resize: vertical;
  }
`;

export const ContactButton = styled.button`
  padding: 12px 16px;
  border: none;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.green[600]};
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
`;
